/**
 * Sign out: end the session on the server, then forget it locally.
 *
 * Clearing the browser's copy alone left the token valid for the rest of its
 * hour, so "Sign out" on a shared machine signed nobody out (D6). The server
 * call comes first; the local clear happens whatever it returns — a network
 * failure or a token the server already ended must never trap someone in a
 * session they are trying to leave.
 *
 * The call is made even with no token in memory (S2-05). The refresh cookie
 * may be all that is left of the session, an access token lives only 900
 * seconds, and POST /auth/logout is public: it revokes the session the
 * cookie names, and the one a still-valid bearer names, and clears the
 * cookie. Skipping it would leave the session for the next person at this
 * machine to be restored into on load.
 *
 * The other tabs are told (refresh.ts), so the same session ends there too
 * instead of on their next request.
 *
 * The call gets its own short timeout (D14). It used to inherit the client's
 * 60 s, so against a server that never answered, the person clicking Sign out
 * watched "Signing out…" for a minute before anything happened.
 */
import { post } from "@/lib/api/client";
import { announceSignedOut } from "@/lib/auth/refresh";
import { useAuthStore } from "@/lib/auth/store";

/** How long sign-out waits for the server before clearing local state anyway. */
export const SIGN_OUT_TIMEOUT_MS = 5_000;

export async function signOut(): Promise<void> {
  const sid = useAuthStore.getState().claims?.sid ?? null;
  try {
    await post<unknown>("/auth/logout", undefined, undefined, {
      timeout: SIGN_OUT_TIMEOUT_MS,
    });
  } catch {
    // Deliberately ignored: the local sign-out below is what the user asked
    // for, and it does not depend on the server agreeing.
  } finally {
    useAuthStore.getState().logout();
    announceSignedOut(sid);
  }
}
